import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Link, useHistory } from 'react-router-dom';
import { SectionProps } from '../utils/SectionProps';
import SectionHeader from '../components/sections/partials/SectionHeader';
import Button from '../components/elements/Button';
import Input from '../components/elements/Input';
import FormHint from '../components/elements/FormHint';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api/client';

const propTypes = {
  ...SectionProps.types
};

const defaultProps = {
  ...SectionProps.defaults
};

const decodeBase64Url = (value) => {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddedValue = normalizedValue.padEnd(Math.ceil(normalizedValue.length / 4) * 4, '=');

  try {
    return window.atob(paddedValue);
  } catch (error) {
    return '';
  }
};

const parseJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  const tokenParts = token.split('.');

  if (tokenParts.length < 2) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64Url(tokenParts[1]));
  } catch (error) {
    return null;
  }
};

const toDisplayValue = (value, fallback = 'Unavailable') => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const trimmedValue = String(value).trim();
  return trimmedValue ? trimmedValue : fallback;
};

const formatRenewalDate = (value) => {
  if (!value) {
    return 'Renewal date unavailable.';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return `Renews on ${value}`;
  }

  const day = parsedDate.getDate();
  const month = parsedDate.toLocaleString('en-GB', { month: 'long' });
  const year = parsedDate.getFullYear();
  const daySuffix = (() => {
    if (day >= 11 && day <= 13) {
      return 'th';
    }

    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  })();

  return `Renews on ${day}${daySuffix} ${month} ${year}`;
};

const resolveConnectedStoreUrl = (accountDetails) => (
  accountDetails.wordpress_site_url
  || accountDetails.site_url
  || accountDetails.connected_site_url
  || accountDetails.store_url
  || accountDetails.website_url
  || ''
);

const Account = ({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor
}) => {
  const history = useHistory();
  const { auth, token, user, logout } = useContext(AuthContext);
  const [accountDetails, setAccountDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [billingError, setBillingError] = useState('');
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isUpgradeHelpVisible, setIsUpgradeHelpVisible] = useState(false);
  const [isJoinHelpVisible, setIsJoinHelpVisible] = useState(false);
  const [linkAccountValue, setLinkAccountValue] = useState('');
  const [linkAccountError, setLinkAccountError] = useState('');
  const [linkAccountSuccess, setLinkAccountSuccess] = useState('');
  const [linkAccountCopyMessage, setLinkAccountCopyMessage] = useState('');
  const [linkAccountCopyStatus, setLinkAccountCopyStatus] = useState('');
  const [isJoinAccountLoading, setIsJoinAccountLoading] = useState(false);

  const jwtPayload = useMemo(() => parseJwtPayload(token), [token]);

  const userId = useMemo(() => {
    if (user && user.id) {
      return String(user.id);
    }

    if (user && user.user_id) {
      return String(user.user_id);
    }

    if (auth && auth.user && auth.user.id) {
      return String(auth.user.id);
    }

    if (auth && auth.user && auth.user.user_id) {
      return String(auth.user.user_id);
    }

    if (auth && auth.id) {
      return String(auth.id);
    }

    if (auth && auth.user_id) {
      return String(auth.user_id);
    }

    if (jwtPayload && jwtPayload.user_id) {
      return String(jwtPayload.user_id);
    }

    if (jwtPayload && jwtPayload.id) {
      return String(jwtPayload.id);
    }

    if (jwtPayload && jwtPayload.sub) {
      return String(jwtPayload.sub);
    }

    return '';
  }, [auth, jwtPayload, user]);

  const normalizedUserId = useMemo(() => (
    /^\d+$/.test(userId) ? Number(userId) : userId
  ), [userId]);

  const loadAccountDetails = useCallback(async () => {
    if (!token || !userId) {
      setLoadError('We could not load your account details right now.');
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    setLoadError('');

    try {
      const response = await apiRequest('/api/v1/user_details', {
        method: 'POST',
        mode: 'cors',
        token,
        body: {
          user_id: normalizedUserId
        }
      });

      setAccountDetails(response);
      return response;
    } catch (error) {
      setLoadError(error.message || 'Unable to load your account details right now.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [normalizedUserId, token, userId]);

  useEffect(() => {
    loadAccountDetails();
  }, [loadAccountDetails]);

  const outerClasses = classNames(
    'signin section',
    topOuterDivider && 'has-top-divider',
    bottomOuterDivider && 'has-bottom-divider',
    hasBgColor && 'has-bg-color',
    invertColor && 'invert-color',
    className
  );

  const innerClasses = classNames(
    'signin-inner section-inner',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider'
  );

  const sectionHeader = {
    title: 'Your Exanta account',
    paragraph: 'You are signed in and ready to access your protected resources.'
  };

  const handleLogout = () => {
    logout();
    history.push('/');
  };

  const handleUpgradeAccount = async () => {
    setBillingError('');
    setIsBillingLoading(true);

    try {
      const response = await apiRequest('/api/v1/create_checkout_session', {
        method: 'POST',
        mode: 'cors',
        token
      });

      const redirectUrl = response && (
        response.checkout_url
        || response.url
        || response.redirect_url
      );

      if (!redirectUrl) {
        throw new Error('Checkout URL unavailable.');
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      setBillingError(error.message || 'Unable to open billing right now.');
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setBillingError('');
    setIsBillingLoading(true);

    try {
      const response = await apiRequest('/api/v1/create_billing_portal_session', {
        method: 'POST',
        mode: 'cors',
        token
      });

      if (!response || !response.portal_url) {
        throw new Error('Billing portal URL unavailable.');
      }

      window.location.href = response.portal_url;
    } catch (error) {
      setBillingError('Could not open billing portal. Please try again.');
    } finally {
      setIsBillingLoading(false);
    }
  };

  const rawTierName = accountDetails && (
    accountDetails.subscription_tier
    || accountDetails.tier_name
  );
  const tierName = toDisplayValue(rawTierName);
  const normalizedTierName = rawTierName ? String(rawTierName).trim().toLowerCase() : '';
  const isProTier = normalizedTierName === 'pro';
  const rawAccountId = accountDetails && accountDetails.account_id;
  const normalizedAccountId = rawAccountId === null || rawAccountId === undefined
    ? ''
    : String(rawAccountId).trim().toLowerCase();
  const rawStringId = accountDetails && accountDetails.string_id;
  const resolvedStringId = rawStringId === null || rawStringId === undefined
    ? ''
    : String(rawStringId).trim();
  const isLinkAccountEditable = normalizedAccountId === '1' && !resolvedStringId;
  const showCopyStringId = !isLinkAccountEditable && Boolean(resolvedStringId);
  const isUpgradeDisabled = normalizedAccountId === '' || normalizedAccountId === 'none' || normalizedAccountId === '1';
  const renewalDate = formatRenewalDate(
    accountDetails && (
      accountDetails.subscription_expiry_date
      || accountDetails.subscriptionExpiryDate
      || accountDetails.renewal_date
      || accountDetails.renewal_at
      || accountDetails.current_period_end
      || accountDetails.subscription_renewal_date
    )
  );
  const monthlyLimit = Number(accountDetails && accountDetails.monthly_limit) || 0;
  const currentMonthQueries = Number(accountDetails && accountDetails.current_month_queries) || 0;
  const usagePercent = monthlyLimit > 0
    ? Math.min(100, Math.round((currentMonthQueries / monthlyLimit) * 100))
    : 0;
  const connectedStoreUrl = toDisplayValue(resolveConnectedStoreUrl(accountDetails || {}), 'No connected store found.');
  const fullName = toDisplayValue([
    accountDetails && accountDetails.first_name,
    accountDetails && accountDetails.last_name
  ].filter(Boolean).join(' '));
  const email = toDisplayValue(accountDetails && accountDetails.email);
  const showManageSubscription = Boolean(accountDetails) && isProTier;

  useEffect(() => {
    if (resolvedStringId) {
      setLinkAccountValue(resolvedStringId);
      setLinkAccountCopyMessage('');
      setLinkAccountCopyStatus('');
      return;
    }

    if (!isLinkAccountEditable) {
      setLinkAccountValue('');
    }

    setLinkAccountCopyMessage('');
    setLinkAccountCopyStatus('');
  }, [isLinkAccountEditable, resolvedStringId]);

  const handleCopyStringId = async () => {
    if (!resolvedStringId) {
      return;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(resolvedStringId);
      } else {
        const fallbackTextArea = document.createElement('textarea');
        fallbackTextArea.value = resolvedStringId;
        fallbackTextArea.setAttribute('readonly', '');
        fallbackTextArea.style.position = 'absolute';
        fallbackTextArea.style.left = '-9999px';
        document.body.appendChild(fallbackTextArea);
        fallbackTextArea.select();
        document.execCommand('copy');
        document.body.removeChild(fallbackTextArea);
      }

      setLinkAccountCopyMessage('Copied to clipboard');
      setLinkAccountCopyStatus('success');
    } catch (error) {
      setLinkAccountCopyMessage('Could not copy account string.');
      setLinkAccountCopyStatus('error');
    }
  };

  const handleJoinAccount = async () => {
    const trimmedStringId = linkAccountValue.trim();

    if (!trimmedStringId) {
      setLinkAccountError('Please enter a link account ID.');
      setLinkAccountSuccess('');
      return;
    }

    setLinkAccountError('');
    setLinkAccountSuccess('');
    setIsJoinAccountLoading(true);

    try {
      const response = await apiRequest('/api/v1/join_account', {
        method: 'POST',
        mode: 'cors',
        token,
        body: {
          string_id: trimmedStringId
        }
      });

      if (!response || response.account_id === null || response.account_id === undefined) {
        throw new Error('Account ID unavailable.');
      }

      await loadAccountDetails();
      setLinkAccountSuccess('Account linked successfully');
    } catch (error) {
      setLinkAccountError(error.message || 'Could not link account.');
    } finally {
      setIsJoinAccountLoading(false);
    }
  };

  return (
    <section className={outerClasses}>
      <div className="container">
        <div className={innerClasses}>
          <SectionHeader tag="h1" data={sectionHeader} className="center-content" />
          <div className="tiles-wrap">
            <div className="tiles-item account-tiles-item">
              <div className="tiles-item-inner has-shadow account-tile">
                {isLoading && (
                  <p className="account-status-message text-sm text-color-low mb-24">
                    Loading your account details...
                  </p>
                )}

                {loadError && (
                  <FormHint className="mb-24" status="error">{loadError}</FormHint>
                )}

                {!isLoading && !loadError && accountDetails && (
                  <React.Fragment>
                    <div className="account-section">
                      <h4 className="mt-0 mb-12">Current plan</h4>
                      <p className="account-plan-name h5 mt-0 mb-12">{tierName}</p>
                      {showManageSubscription ? (
                        <React.Fragment>
                          <Button
                            color="dark"
                            type="button"
                            wideMobile
                            loading={isBillingLoading}
                            disabled={isBillingLoading}
                            onClick={handleManageSubscription}
                          >
                            {isBillingLoading ? 'Loading...' : 'Manage Subscription'}
                          </Button>
                          <p className="account-inline-copy text-sm text-color-low mt-12 mb-0">
                            {renewalDate}
                          </p>
                        </React.Fragment>
                      ) : (
                        <div className="account-plan-action">
                          <Button
                            color="primary"
                            type="button"
                            wideMobile
                            loading={isBillingLoading}
                            disabled={isBillingLoading || isUpgradeDisabled}
                            onClick={handleUpgradeAccount}
                          >
                            {isBillingLoading ? 'Loading...' : 'Upgrade to Pro'}
                          </Button>
                          {isUpgradeDisabled && (
                            <span className={classNames(
                              'account-upgrade-help',
                              isUpgradeHelpVisible && 'is-active'
                            )}>
                              <button
                                type="button"
                                className="account-upgrade-help-toggle"
                                aria-label="Why is upgrade disabled?"
                                aria-expanded={isUpgradeHelpVisible}
                                onClick={() => setIsUpgradeHelpVisible((currentValue) => !currentValue)}
                              >
                                i
                              </button>
                              <span className="account-upgrade-help-tooltip" role="tooltip">
                                To upgrade, you&apos;ll need to connect a site first. <Link to="/download" className="func-link">Download Exanta Analytics</Link>, install it on your WordPress site, and run Map Database, then come back here to upgrade.
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                      <div className="account-link-section mt-20">
                        <h5 className="mt-0 mb-12">Link Account</h5>
                        <div className="mb-16">
                          <Input
                            id="account-link-id"
                            type="text"
                            label="Account link ID"
                            hasIcon={showCopyStringId ? 'right' : null}
                            className={classNames(showCopyStringId && 'account-link-input-copy')}
                            value={linkAccountValue}
                            disabled={!isLinkAccountEditable || isJoinAccountLoading}
                            onChange={(event) => {
                              setLinkAccountValue(event.target.value);
                              setLinkAccountError('');
                              setLinkAccountSuccess('');
                              setLinkAccountCopyMessage('');
                              setLinkAccountCopyStatus('');
                            }}
                          >
                            {showCopyStringId && (
                              <button
                                type="button"
                                className="account-link-copy-button"
                                onClick={handleCopyStringId}
                                aria-label="Copy account link ID"
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                                  <rect x="5" y="3" width="8" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                  <rect x="3" y="5" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                </svg>
                              </button>
                            )}
                          </Input>
                        </div>
                        {linkAccountCopyMessage && (
                          <FormHint className="mt-n12 mb-16" status={linkAccountCopyStatus}>{linkAccountCopyMessage}</FormHint>
                        )}
                        {isLinkAccountEditable && (
                          <div className="account-plan-action">
                            <Button
                              color="dark"
                              type="button"
                              wideMobile
                              loading={isJoinAccountLoading}
                              disabled={!isLinkAccountEditable || isJoinAccountLoading || !linkAccountValue.trim()}
                              onClick={handleJoinAccount}
                            >
                              {isJoinAccountLoading ? 'Loading...' : 'Join Account'}
                            </Button>
                            <span className={classNames(
                              'account-upgrade-help',
                              isJoinHelpVisible && 'is-active'
                            )}>
                              <button
                                type="button"
                                className="account-upgrade-help-toggle"
                                aria-label="How does joining an account work?"
                                aria-expanded={isJoinHelpVisible}
                                onClick={() => setIsJoinHelpVisible((currentValue) => !currentValue)}
                              >
                                i
                              </button>
                              <span className="account-upgrade-help-tooltip" role="tooltip">
                                If your account is part of a larger organisation (more than 1 user) then please paste the account string in this box and click &apos;Join Account&apos;. You will then be able to access the account&apos;s subscription. You can find the account string inside this box for any user already linked.
                                <br />
                                <br />
                                If you account is only you then you can ignore this box.
                              </span>
                            </span>
                          </div>
                        )}
                        {linkAccountSuccess && (
                          <FormHint className="mt-12" status="success">{linkAccountSuccess}</FormHint>
                        )}
                        {linkAccountError && (
                          <FormHint className="mt-12" status="error">{linkAccountError}</FormHint>
                        )}
                      </div>
                      {billingError && (
                        <FormHint className="mt-12" status="error">{billingError}</FormHint>
                      )}
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-12">Usage this month</h4>
                      <p className="account-inline-copy text-sm text-color-low mt-0 mb-16">
                        {monthlyLimit > 0
                          ? `${currentMonthQueries} of ${monthlyLimit} queries used this month`
                          : `${currentMonthQueries} queries used this month`}
                      </p>
                      <div className="account-usage-bar" aria-hidden="true">
                        <div
                          className="account-usage-bar-fill"
                          style={{ width: `${usagePercent}%` }}
                        />
                      </div>
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-16">Connected store</h4>
                      <Input
                        id="account-connected-store"
                        type="text"
                        label="WordPress site URL"
                        value={connectedStoreUrl}
                        readOnly
                      />
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-16">Account details</h4>
                      <div className="mb-16">
                        <Input
                          id="account-name"
                          type="text"
                          label="Name"
                          value={fullName}
                          readOnly
                        />
                      </div>
                      <div className="mb-16">
                        <Input
                          id="account-email"
                          type="email"
                          label="Email"
                          value={email}
                          readOnly
                        />
                      </div>
                      <Link to="/forgotpassword" className="func-link">Change password</Link>
                    </div>

                    <div className="account-section">
                      <h4 className="mt-0 mb-16">Actions</h4>
                      <div className="mb-16">
                        <Button tag={Link} to="/feedback" color="primary" wide>
                          Fill Out Feedback Form
                        </Button>
                      </div>
                    </div>
                  </React.Fragment>
                )}

                <div className="mb-16">
                  <Button color="dark" wide onClick={handleLogout}>
                    Logout
                  </Button>
                </div>
                <Button tag={Link} to="/download" color="primary" wide>
                  Go to Download
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

Account.propTypes = propTypes;
Account.defaultProps = defaultProps;

export default Account;
